import React from "react";

import type { Analysis } from "../../../../api/Analysis";
import type { SeriesLineOptions } from "highcharts";
import { MEASURE_POINT_METADATA, type MeasurePointMetadata } from "../../Chart/PointMetadata";
import seriesColors from "../../Chart/series/seriesColors";
import { metrologyValue as getMetrologyValue } from "diagnostics-domain/quantity/metrology";
import type { QuantityWithMetrology } from "diagnostics-domain/quantity/metrology";
import type { MetrologyOverrides } from "../../../../api/useMetrologyOverrides";

type AnalysisPoint = {
  label: string | null;
  date: number;
  id: string;
  group: string | null;
  active?: boolean;
  quantityValues: Record<string, ExpressionEngineResult>;
};

type ExpressionEngineResult = {
  std: number | null;
  inputCount: number | null;
  missingSmartArgsCount: number | null;
  isSmart: boolean;
  measured: number;
};

function getPoints(
  analyses: AnalysisPoint[],
  selectedQuantity: QuantityWithMetrology & { unit: string | null },
  metrologyOverrides: MetrologyOverrides,
) {
  return analyses.reduce((pointArray: Highcharts.PointOptionsObject[], analysis, index) => {
    const previousAnalysis = analyses[index - 1];
    const quantityValue = analysis.quantityValues[selectedQuantity.id];

    if (!quantityValue) {
      const pointMetadata: MeasurePointMetadata = {
        type: MEASURE_POINT_METADATA,
        human: null,
        group: analysis.group,
        label: analysis.label,
        analysisId: analysis.id,
        date: analysis.date,
        quantityId: selectedQuantity.id,
        quantityName: selectedQuantity.name,
        validity: "NO_VALIDATION",
        publishedAnalysis: false,
        active: analysis.active,
      };
      pointArray.push({
        id: `analysis-id-${analysis.id}`,
        x: analysis.date,
        y: null,
        custom: pointMetadata,
      });
      return pointArray;
    }

    if (previousAnalysis !== undefined && previousAnalysis.group !== analysis.group) {
      pointArray.push({
        id: `cycle-middlepoint-${previousAnalysis.group}-${analysis.group}`,
        x: (analysis.date + previousAnalysis.date) / 2,
        y: null,
        allowPointSelect: false,
      });
    }

    const metrologyValue = getMetrologyValue(
      selectedQuantity,
      analysis.quantityValues,
      metrologyOverrides[analysis.date],
    );
    const pointMetadata: MeasurePointMetadata = {
      type: MEASURE_POINT_METADATA,
      group: analysis.group,
      label: analysis.label,
      analysisId: analysis.id,
      date: analysis.date,
      active: analysis.active,
      human:
        quantityValue.measured === null
          ? null
          : {
              value: quantityValue.measured,
              unit: selectedQuantity.unit,
              precision: 2,
              sign: null,
            },
      validity: metrologyValue ? metrologyValue.status : "NO_VALIDATION",
      publishedAnalysis: false,
      quantityId: selectedQuantity.id,
      quantityName: selectedQuantity.name,
    };
    if (metrologyValue ? metrologyValue.invalid : false) {
      pointArray.push({
        id: `analysis-id-${analysis.id}-invalid-hidden`,
        x: analysis.date,
        y: null,
        custom: pointMetadata,
      });
      return pointArray;
    }

    const { measured } = quantityValue;

    pointArray.push({
      id: `analysis-id-${analysis.id}`,
      x: analysis.date,
      y: measured,
      custom: pointMetadata,
      className: selectedQuantity.name,
      marker: {
        enabled: analysis.active,
        symbol: "circle",
        lineColor: seriesColors.sparklineMarker,
        fillColor: seriesColors.sparklineMarker,
        lineWidth: 2,
        radius: 2,
        states: {
          hover: {
            enabled: true,
            radius: 3,
          },
        },
      },
    });

    return pointArray;
  }, []);
}
export function getMeasureSeries({
  yAxisId,
  analyses,
  selectedQuantity,
  metrologyOverrides,
}: {
  yAxisId: string;
  analyses: AnalysisPoint[];
  selectedQuantity: QuantityWithMetrology & { unit: string | null };
  metrologyOverrides: MetrologyOverrides;
}): SeriesLineOptions {
  return {
    id: `production-monitoring-measure-serie-${selectedQuantity.name}`,
    name: selectedQuantity.name,
    data: getPoints(analyses, selectedQuantity, metrologyOverrides),
    type: "line",
    replicateInNavigator: false,
    yAxis: yAxisId,
    color: seriesColors.sparklineArea,
    lineColor: seriesColors.sparklineArea,
  } as SeriesLineOptions;
}

function useSeries(
  yAxisId: string,
  analyses: Analysis[],
  selectedQuantity: (QuantityWithMetrology & { unit: string | null }) | null,
  metrologyOverrides: MetrologyOverrides,
) {
  return React.useMemo(() => {
    const measureSeries = selectedQuantity
      ? getMeasureSeries({
          yAxisId,
          analyses,
          selectedQuantity,
          metrologyOverrides,
        })
      : [];

    return [measureSeries];
  }, [analyses, selectedQuantity, yAxisId, metrologyOverrides]);
}

export default useSeries;
